import {t, useLanguage} from '../i18n';
import React, { useEffect, useMemo, useState } from "react";
// LOOP BUSINESS OS V3 — calmer header, no access bubble, loyalty included, request logging
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { loopBusinessNow, loopBusinessUpdateDoc } from "../utils/businessNativeSafeWrites";

import { loopBusinessIsPlatformOwner } from "../utils/businessNativeSafeWrites";
import { db as defaultDb, auth } from "../firebase";
import { normalizeLoopBusinessSettings } from "../adminCalendar/workerPermissions";

const EMPTY_BUSINESS = {};
const OWNER_ROLES = new Set(["owner", "admin", "manager", "superadmin", "loop_admin"]);

const MODULES = [
  {
    key: "analytics",
    title: "Reportes",
    copy: "Reportes, tablero y salud del negocio.",
    group: "core",
    control: "owner",
    tag: "Incluido",
  },
  {
    key: "inventory",
    title: "Inventario",
    copy: "Productos, stock y alertas operativas.",
    group: "core",
    control: "owner",
    tag: "Incluido",
  },
  {
    key: "crm",
    title: "Clientes",
    copy: "Base inteligente de clientes y actividad.",
    group: "core",
    control: "owner",
    tag: "Incluido",
  },
  {
    key: "auditLogs",
    title: "Auditoría",
    copy: "Historial interno de cambios importantes.",
    group: "core",
    control: "owner",
    tag: "Incluido",
  },
  {
    key: "whatsappManual",
    title: "WhatsApp manual",
    copy: "Plantillas y mensajes manuales desde Loop.",
    group: "protected",
    control: "loop",
    tag: "Controlado por Loop",
  },
  {
    key: "payments",
    title: "Pagos online",
    copy: "Adelantos, links de pago y proveedores.",
    group: "protected",
    control: "loop",
    tag: "Requiere activación",
  },
  {
    key: "loyalty",
    title: "Fidelidad",
    copy: "Cliente frecuente, puntos y beneficios.",
    group: "core",
    control: "owner",
    tag: "Incluido",
    defaultEnabled: true,
  },
  {
    key: "calendarSync",
    title: "Sincronización de calendario",
    copy: "Integraciones externas de calendario.",
    group: "protected",
    control: "loop",
    tag: "Próximamente",
  },
];

function planLabel(value) {
  const v = String(value || "early_access").trim();
  if (v === "early_access") return "Early Access";
  if (v === "premium") return "Loop Premium";
  if (v === "pro") return "Loop Pro";
  return v || "Early Access";
}

function roleLabel(role, canEditOperational, canEditLoopManaged) {
  if (canEditLoopManaged) return "Loop Admin";
  if (canEditOperational) return "Dueño / admin del negocio";
  const clean = String(role || "").trim();
  return clean ? "Solo lectura" : "Solo lectura";
}

function moduleIsRequested(businessDoc = {}, key = "") {
  const req = businessDoc.businessOsRequests?.[key] || businessDoc.moduleRequests?.[key] || null;
  const status = String(req?.status || "").toLowerCase();
  return ["requested", "pending", "open"].includes(status);
}

function normalizeBusinessOsSettingsV3(source = {}) {
  return normalizeLoopBusinessSettings(source);
}

function getModuleRequestSnapshot(businessDoc = {}) {
  return businessDoc.businessOsRequests || businessDoc.moduleRequests || {};
}

export default function BusinessOsSection({
  openPayments = false,
  businessId,
  businessDoc = EMPTY_BUSINESS,
  db = defaultDb,
  onBusinessPatch = () => {},
  userRole = "",
  canManageBusinessOs = null,
  isLoopAdmin = false,
  readOnly = false,
}) {
  useLanguage();
  const [settings, setSettings] = useState(() => normalizeBusinessOsSettingsV3(businessDoc));
  const [view, setView] = useState("modules");
  const [saving, setSaving] = useState("");


  useEffect(() => setSettings(normalizeBusinessOsSettingsV3(businessDoc)), [businessDoc]);

  const requestActor = useMemo(() => {
    const current = auth?.currentUser || null;
    return {
      uid: current?.uid || businessDoc.lastUpdatedByUid || "",
      email: current?.email || businessDoc.lastUpdatedByEmail || "",
      name: current?.displayName || businessDoc.lastUpdatedByName || "",
    };
  }, [businessDoc]);

  const inferredRole = useMemo(() => {
    return String(
      userRole ||
        businessDoc.currentUserRole ||
        businessDoc.viewerRole ||
        businessDoc.myRole ||
        businessDoc.role ||
        ""
    ).trim().toLowerCase();
  }, [userRole, businessDoc]);

  const effectiveLoopAdmin = Boolean(isLoopAdmin && loopBusinessIsPlatformOwner());
  const inferredCanManage = canManageBusinessOs === null || canManageBusinessOs === undefined
    ? OWNER_ROLES.has(inferredRole)
    : Boolean(canManageBusinessOs);

  const canEditOperational = Boolean(!readOnly && (effectiveLoopAdmin || inferredCanManage));
  const canEditLoopManaged = Boolean(!readOnly && effectiveLoopAdmin);
  const normalizedSettings = normalizeBusinessOsSettingsV3(settings);
  const enabledModules = normalizedSettings.enabledModules || {};
  const activeCount = MODULES.filter((mod) => Boolean(enabledModules[mod.key])).length;
  const lockedCount = MODULES.filter((mod) => mod.control === "loop" && !Boolean(enabledModules[mod.key])).length;

  const persistSection = async (section, nextSection) => {
    if (!businessId) {
      setSaving("Guarda el negocio antes de cambiar la configuración.");
      return;
    }
    setSaving("Guardando...");
    try {
      await loopBusinessUpdateDoc(db || defaultDb, `businesses/${businessId}`, {
        [section]: nextSection,
        updatedAt: loopBusinessNow(),
      });
      onBusinessPatch({ [section]: nextSection });
      setSaving("Guardado");
      window.setTimeout(() => setSaving(""), 1200);
    } catch (err) {
      console.error("[BusinessOsSection] save failed", err);
      setSaving("No se pudo guardar");
      setSettings(normalizeBusinessOsSettingsV3(businessDoc));
    }
  };

  const updateSetting = (section, key, value, { requiresLoopAdmin = false } = {}) => {
    if (requiresLoopAdmin && !canEditLoopManaged) {
      setSaving("Este módulo lo activa Loop.");
      window.setTimeout(() => setSaving(""), 1800);
      return;
    }
    if (!requiresLoopAdmin && !canEditOperational) {
      setSaving("Solo el dueño o admin del negocio puede cambiar esto.");
      window.setTimeout(() => setSaving(""), 1800);
      return;
    }
    const current = normalizeBusinessOsSettingsV3(settings);
    const nextSection = { ...(current[section] || {}), [key]: value };
    const nextSettings = { ...current, [section]: nextSection };
    setSettings(nextSettings);
    persistSection(section, nextSection);
  };

  const requestModuleAccess = async (mod) => {
    if (!businessId) {
      setSaving("Guarda el negocio antes de solicitar activación.");
      return;
    }
    if (moduleIsRequested(businessDoc, mod.key)) {
      setSaving("Solicitud ya registrada para Loop.");
      window.setTimeout(() => setSaving(""), 1600);
      return;
    }
    setSaving("Registrando solicitud...");
    const requestPayload = {
      moduleKey: mod.key,
      title: mod.title,
      status: "requested",
      requestedByUid: requestActor.uid || null,
      requestedByEmail: requestActor.email || null,
      requestedByName: requestActor.name || null,
      source: "business_os",
    };

    try {
      await loopBusinessUpdateDoc(db || defaultDb, `businesses/${businessId}`, {
        [`businessOsRequests.${mod.key}.moduleKey`]: requestPayload.moduleKey,
        [`businessOsRequests.${mod.key}.title`]: requestPayload.title,
        [`businessOsRequests.${mod.key}.status`]: requestPayload.status,
        [`businessOsRequests.${mod.key}.requestedByUid`]: requestPayload.requestedByUid,
        [`businessOsRequests.${mod.key}.requestedByEmail`]: requestPayload.requestedByEmail,
        [`businessOsRequests.${mod.key}.requestedByName`]: requestPayload.requestedByName,
        [`businessOsRequests.${mod.key}.source`]: requestPayload.source,
        [`businessOsRequests.${mod.key}.requestedAt`]: loopBusinessNow(),
        [`businessOsRequests.${mod.key}.updatedAt`]: loopBusinessNow(),
        lastBusinessOsRequest: {
          moduleKey: mod.key,
          title: mod.title,
          status: "requested",
          source: "business_os",
          requestedByEmail: requestPayload.requestedByEmail,
        },
        businessOsRequestsUpdatedAt: loopBusinessNow(),
        updatedAt: loopBusinessNow(),
      });
      const nextRequests = {
        ...getModuleRequestSnapshot(businessDoc),
        [mod.key]: { ...requestPayload },
      };
      onBusinessPatch({
        businessOsRequests: nextRequests,
        lastBusinessOsRequest: {
          moduleKey: mod.key,
          title: mod.title,
          status: "requested",
          source: "business_os",
          requestedByEmail: requestPayload.requestedByEmail,
        },
      });
      try {
        window.__LOOP_BUSINESS_OS_LAST_REQUEST__ = {
          businessId,
          moduleKey: mod.key,
          title: mod.title,
          status: "requested",
          time: new Date().toISOString(),
          note: "Guardado en businesses/{businessId}.businessOsRequests. Aún necesita inbox/notificación interna de Loop para alertar al owner de la plataforma.",
        };
      } catch (_) {}
      setSaving("Solicitud registrada para Loop");
      window.setTimeout(() => setSaving(""), 1800);
    } catch (err) {
      console.error("[BusinessOsSection] request module failed", err);
      setSaving("No se pudo registrar la solicitud");
    }
  };

  const Switch = ({ section, fieldKey, title, copy, disabled = false, loopOnly = false }) => {
    const checked = Boolean(normalizedSettings?.[section]?.[fieldKey]);
    const canEdit = loopOnly ? canEditLoopManaged : canEditOperational;
    const isDisabled = disabled || !canEdit || saving === "Guardando...";
    return (
      <button
        type="button"
        aria-pressed={checked}
        className={`bos-switch ${checked ? "is-on" : "is-off"} ${isDisabled ? "is-disabled" : ""}`}
        onClick={() => !isDisabled && updateSetting(section, fieldKey, !checked, { requiresLoopAdmin: loopOnly })}
        disabled={isDisabled}
        title={isDisabled ? t("Solo lectura") : title}
      >
        <span className="bos-toggle"><i /></span>
        <span><strong>{title}</strong><em>{copy}</em></span>
      </button>
    );
  };

  const ModuleCard = ({ mod }) => {
    const active = Boolean(enabledModules[mod.key]);
    const requested = moduleIsRequested(businessDoc, mod.key);
    const ownerEditable = mod.control === "owner" && canEditLoopManaged;
    const loopEditable = mod.control === "loop" && canEditLoopManaged;
    const editable = ownerEditable || loopEditable;
    const lockedForBusiness = mod.control === "loop" && !loopEditable;
    const status = active
      ? (mod.control === "loop" ? "Activado por Loop" : "Activo")
      : requested
        ? "Solicitud enviada"
        : lockedForBusiness
          ? mod.tag
          : "Inactivo";

    const handleClick = () => {
      if (editable) {
        updateSetting("enabledModules", mod.key, !active, { requiresLoopAdmin: mod.control === "loop" });
        return;
      }
      
    };

    return (
      <button
        type="button"
        className={`bos-module-card ${active ? "is-active" : ""} ${lockedForBusiness ? "is-locked" : ""} ${requested ? "is-requested" : ""}`}
        data-module={mod.key}
        onClick={handleClick}
        disabled={!editable || saving === "Guardando..." || mod.key === "calendarSync"}
      >
        <span className="bos-module-topline">
          <span className={`bos-module-dot ${active ? "is-on" : ""}`}>{lockedForBusiness && !active ? "🔒" : active ? "✓" : ""}</span>
          <em>{status}</em>
        </span>
        <strong>{mod.title}</strong>
        <small>{mod.copy}</small>
        
      </button>
    );
  };

  const SelectField = ({ section, fieldKey, label, children, loopOnly = false }) => {
    const canEdit = loopOnly ? canEditLoopManaged : canEditOperational;
    return (
      <label className={`bos-field ${!canEdit ? "is-disabled" : ""}`}>
        <span>{label}</span>
        <select
          value={normalizedSettings?.[section]?.[fieldKey] || ""}
          onChange={(e) => updateSetting(section, fieldKey, e.target.value, { requiresLoopAdmin: loopOnly })}
          disabled={!canEdit}
        >
          {children}
        </select>
      </label>
    );
  };

  return (
    <div className="bos-page bos-v2 bos-compact">
      <style>{styles}</style>

      <nav className="bos-view-tabs" aria-label={t("Configuración de funciones")}>{[['modules','Funciones disponibles'],['preferences','Preferencias operativas']].map(([id,label])=><button type="button" key={id} aria-pressed={view===id} onClick={()=>setView(id)}>{label}</button>)}</nav>
      {view === 'modules' && <><div className="bos-compact-intro"><strong>{t("Funciones disponibles")}</strong><p>{t("La disponibilidad la administra Loop. Configura abajo cómo trabaja tu negocio.")}</p><small role="status">{saving || "Las preferencias se guardan al cambiarlas."}</small></div>
      <section className="bos-card"><div className="bos-module-grid bos-module-grid-v2">{MODULES.filter(mod=>mod.key!=="payments").map(mod=><ModuleCard key={mod.key} mod={mod}/>)}</div><p className="bos-note">{t("La disponibilidad de una función no confirma la conexión de servicios externos. No se realiza ningún cobro por cambiar estas preferencias.")}</p></section>
      </>}
      {view === "preferences" && <section aria-label={t("Preferencias operativas")}>
      <div className="bos-grid bos-grid-v2">
        <section className="bos-card">
          <div className="bos-card-title"><strong>{t("Finanzas")}</strong><span>{t("Caja, comisiones y visibilidad para recepción.")}</span></div>
          <SelectField section="financeSettings" fieldKey="receptionFinanceVisibility" label={t("Recepción ve finanzas")}>
            <option value="full">{t("Completo: cobros, comisiones y neto")}</option>
            <option value="payment_only">{t("Solo cobros y métodos de pago")}</option>
            <option value="hidden">{t("Oculto para recepción")}</option>
          </SelectField>
          <Switch section="financeSettings" fieldKey="showPaymentMethods" title={t("Mostrar métodos de pago")} copy="Efectivo, Yappy, tarjeta, transferencia." />
          <Switch section="financeSettings" fieldKey="showWorkerPayouts" title={t("Mostrar pagos a trabajadores")} copy="Permite ver comisiones y propinas." />
          <Switch section="financeSettings" fieldKey="allowManualPayoutOverride" title={t("Ajuste manual de payout")} copy="Dueño/admin puede corregir montos." />
        </section>

        <section className="bos-card">
          <div className="bos-card-title"><strong>{t("Inventario")}</strong><span>{t("Stock, descuento automático y reglas de inventario.")}</span></div>
          <Switch section="inventorySettings" fieldKey="enabled" title={t("Inventario activo")} copy="Permite productos y stock." />
          <Switch section="inventorySettings" fieldKey="autoDeduct" title={t("Descuento automático")} copy="Baja stock según reservas/ventas." />
          <SelectField section="inventorySettings" fieldKey="deductTrigger" label={t("Descontar stock al")}>
            <option value="completed">{t("Marcar reserva como completada")}</option>
            <option value="paid">{t("Marcar pago como cobrado")}</option>
          </SelectField>
          <Switch section="inventorySettings" fieldKey="allowNegativeStock" title={t("Permitir stock negativo")} copy="Recomendado mantener apagado." />
        </section>

        <section className="bos-card">
          <div className="bos-card-title"><strong>Trabajadores</strong><span>{t("Qué ve el equipo por defecto sobre compensación.")}</span></div>
          <SelectField section="workerSettings" fieldKey="compensationVisibilityDefault" label={t("Visibilidad default de compensación")}>
            <option value="hidden">{t("Oculto: solo agenda")}</option>
            <option value="tips_only">{t("Solo propinas")}</option>
            <option value="payout_total">{t("Pago total estimado")}</option>
            <option value="full_breakdown">{t("Desglose completo")}</option>
          </SelectField>
          <div className="bos-note">{t("Este valor puede cambiarse por trabajador desde la sección Equipo.")}</div>
        </section>

        <section className="bos-card"><div className="bos-card-title"><strong>{t("Historial de cambios")}</strong><span>{t("Registro de actividad del negocio.")}</span></div><Switch section="auditSettings" fieldKey="enabled" title={t("Registrar cambios")} copy="Conserva el historial operativo." loopOnly /></section>
      </div>
      </section>}
    </div>
  );
}

const styles = `
.bos-view-tabs{display:flex;gap:8px;border-bottom:1px solid #dde7e7;padding-bottom:10px;flex-wrap:wrap}.bos-view-tabs button{padding:12px;border:0;border-radius:8px;background:transparent;color:#48635f;cursor:pointer}.bos-view-tabs button[aria-pressed=true]{background:#e9f4f1;color:#00675f;font-weight:600}

.bos-page{display:grid;gap:18px;color:#0f172a}.bos-v2{--bos-brand:#00675f;--bos-brand-soft:#ecfdf5;--bos-line:#e2e8f0;--bos-muted:#64748b}.bos-head{border:1px solid rgba(226,232,240,.92);background:linear-gradient(135deg,rgba(0,103,95,.06),rgba(255,248,239,.75));border-radius:28px;padding:24px;display:flex;justify-content:space-between;gap:16px;align-items:flex-start}.bos-head-v2{align-items:center;background:radial-gradient(circle at 0% 0%,rgba(0,103,95,.10),transparent 32%),linear-gradient(135deg,#fff,#fbfffd 58%,#fff8ef);box-shadow:0 18px 48px rgba(15,23,42,.055)}.bos-head-copy span,.bos-access-card span,.bos-status-card span{font-size:10px;font-weight:950;letter-spacing:.14em;text-transform:uppercase;color:#00675f}.bos-head h1{margin:7px 0 6px;font-size:36px;line-height:1;font-weight:950;letter-spacing:-.07em}.bos-head p{margin:0;color:#64748b;font-weight:760;line-height:1.45;max-width:680px}.bos-status-grid{display:grid;grid-template-columns:repeat(3,minmax(112px,1fr));gap:10px;min-width:410px}.bos-status-card{border:1px solid rgba(226,232,240,.92);background:rgba(255,255,255,.82);border-radius:20px;padding:12px 13px;box-shadow:0 10px 24px rgba(15,23,42,.045)}.bos-status-card strong{display:block;margin-top:8px;color:#0f172a;font-size:20px;line-height:1;font-weight:950;letter-spacing:-.045em}.bos-access-card{border:1px solid rgba(20,184,166,.20);background:linear-gradient(135deg,#f0fdfa,#fff);border-radius:22px;padding:15px 17px;display:flex;align-items:center;justify-content:space-between;gap:14px}.bos-access-card strong{display:block;margin-top:5px;font-size:17px;line-height:1;font-weight:950;letter-spacing:-.035em}.bos-access-card p{margin:5px 0 0;color:#64748b;font-size:12.5px;font-weight:780;line-height:1.35}.bos-access-card em{font-style:normal;color:#00675f;font-size:12px;font-weight:950;white-space:nowrap}.bos-card{border:1px solid rgba(226,232,240,.95);border-radius:24px;background:rgba(255,255,255,.96);box-shadow:0 18px 48px rgba(15,23,42,.055);padding:18px}.bos-card-title{display:grid;gap:4px;margin-bottom:14px}.bos-card-title-row{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}.bos-card-title strong{font-size:17px;font-weight:950;letter-spacing:-.035em}.bos-card-title span{font-size:12.5px;color:#64748b;font-weight:760}.bos-module-columns{display:grid;grid-template-columns:1fr 1fr;gap:15px}.bos-module-column{border:1px solid rgba(226,232,240,.76);background:#fbfdff;border-radius:22px;padding:13px}.bos-mini-title{display:grid;gap:4px;margin-bottom:11px}.bos-mini-title strong{font-size:13.5px;font-weight:950;color:#0f172a}.bos-mini-title span{font-size:11.5px;font-weight:760;color:#64748b}.bos-module-grid-v2{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.bos-module-card{min-height:126px;border:1px solid rgba(226,232,240,.95);background:#fff;border-radius:20px;padding:13px;display:grid;gap:8px;text-align:left;cursor:pointer;box-shadow:0 10px 24px rgba(15,23,42,.04);transition:transform .14s ease,border-color .14s ease,box-shadow .14s ease,background .14s ease}.bos-module-card:hover:not(:disabled){transform:translateY(-1px);border-color:rgba(0,103,95,.24);box-shadow:0 14px 30px rgba(15,23,42,.065)}.bos-module-card:disabled{cursor:default}.bos-module-card.is-active{border-color:rgba(20,184,166,.30);background:linear-gradient(135deg,#f0fdfa,#fff)}.bos-module-card.is-locked{background:linear-gradient(135deg,#fff,#f8fafc)}.bos-module-card.is-requested{border-color:rgba(245,158,11,.28);background:linear-gradient(135deg,#fffbeb,#fff)}.bos-module-topline{display:flex;align-items:center;justify-content:space-between;gap:8px}.bos-module-dot{width:25px;height:25px;border-radius:999px;border:1px solid #cbd5e1;background:#f8fafc;display:grid;place-items:center;color:#64748b;font-size:11px;font-weight:950}.bos-module-dot.is-on{border-color:#00675f;background:#00675f;color:#fff}.bos-module-topline em{font-style:normal;color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.07em;text-align:right}.bos-module-card strong{display:block;font-size:14px;line-height:1.1;font-weight:950;color:#0f172a;letter-spacing:-.025em}.bos-module-card small{display:block;color:#64748b;font-size:11.5px;line-height:1.3;font-weight:760}.bos-module-card b{display:inline-flex;width:max-content;max-width:100%;align-items:center;min-height:27px;border-radius:999px;border:1px solid rgba(0,103,95,.16);background:#ecfdf5;color:#00675f;padding:0 9px;font-size:10.5px;font-weight:950}.bos-grid-v2{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}.bos-switch{border:1px solid rgba(226,232,240,.95);background:#fff;border-radius:18px;padding:12px;display:flex;align-items:center;gap:10px;text-align:left;cursor:pointer;min-height:72px;box-shadow:0 10px 26px rgba(15,23,42,.04);width:100%}.bos-switch.is-on{background:rgba(0,103,95,.045);border-color:rgba(0,103,95,.22)}.bos-switch.is-disabled{opacity:.62;cursor:not-allowed;box-shadow:none}.bos-toggle{width:38px;min-width:38px;height:22px;border-radius:999px;background:#cbd5e1;position:relative}.bos-toggle i{position:absolute;top:3px;left:3px;width:16px;height:16px;border-radius:999px;background:#fff;box-shadow:0 2px 7px rgba(15,23,42,.22);transition:left .18s ease}.bos-switch.is-on .bos-toggle{background:#00675f}.bos-switch.is-on .bos-toggle i{left:19px}.bos-switch strong{display:block;font-size:12.5px;font-weight:950;line-height:1.1}.bos-switch em{display:block;margin-top:3px;font-size:11.5px;font-style:normal;color:#64748b;font-weight:750;line-height:1.25}.bos-field{display:grid;gap:7px;margin-bottom:10px}.bos-field span{font-size:12px;font-weight:900;color:#475569}.bos-field select,.bos-field input{width:100%;min-height:44px;border-radius:14px;border:1px solid #e2e8f0;background:#fff;color:#0f172a;font-size:13px;font-weight:850;padding:0 12px;box-sizing:border-box;outline:none}.bos-field.is-disabled{opacity:.62}.bos-field select:disabled,.bos-field input:disabled{cursor:not-allowed;background:#f8fafc}.bos-note{padding:10px 12px;border-radius:16px;background:#f8fafc;color:#64748b;font-size:12px;font-weight:800}.bos-two{display:grid;grid-template-columns:1fr 120px;gap:10px}@media(max-width:1180px){.bos-head-v2{display:grid}.bos-status-grid{min-width:0;width:100%}.bos-module-columns,.bos-grid-v2{grid-template-columns:1fr}}@media(max-width:760px){.bos-head{padding:18px;border-radius:24px}.bos-head h1{font-size:30px}.bos-status-grid{grid-template-columns:1fr}.bos-access-card{display:grid}.bos-access-card em{white-space:normal}.bos-module-grid-v2{grid-template-columns:1fr}.bos-two{grid-template-columns:1fr}}

/* =========================================================
   LOOP BUSINESS OS V3 — calmer hero, no access bubble, loyalty included
   ========================================================= */
.bos-head-v3{
  min-height:110px!important;
  padding:20px 24px!important;
  align-items:center!important;
  background:radial-gradient(circle at 0% 0%,rgba(0,103,95,.08),transparent 30%),linear-gradient(135deg,#ffffff,#fbfffd 62%,#fff8ef)!important;
}
.bos-head-v3 .bos-head-copy{display:grid!important;gap:7px!important;min-width:0!important;}
.bos-head-v3 .bos-head-copy p{max-width:620px!important;font-size:13.5px!important;line-height:1.42!important;margin:0!important;}
.bos-head-v3 .bos-status-grid{grid-template-columns:repeat(3,minmax(108px,1fr)) minmax(142px,1.25fr)!important;min-width:min(650px,52vw)!important;}
.bos-status-card-wide strong{font-size:15px!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;}
.bos-access-card{display:none!important;}
.bos-card-title-row{display:grid!important;gap:4px!important;}
.bos-card-title-row span{display:block!important;margin-top:3px!important;}
.bos-module-card[data-module="loyalty"]{border-color:rgba(20,184,166,.30)!important;background:linear-gradient(135deg,#f0fdfa,#ffffff)!important;}
@media(max-width:1180px){.bos-head-v3{display:grid!important}.bos-head-v3 .bos-status-grid{min-width:0!important;width:100%!important;grid-template-columns:repeat(4,minmax(0,1fr))!important}}
@media(max-width:760px){.bos-head-v3{padding:17px!important}.bos-head-v3 .bos-status-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important}.bos-status-card-wide strong{font-size:14px!important}}


.bos-compact .bos-compact-intro{padding:4px 0}.bos-compact-intro strong{font-size:17px}.bos-compact-intro p,.bos-compact-intro small{font-size:13px;color:#63796d}
.bos-compact .bos-card{border-radius:10px!important;box-shadow:none!important;padding:18px!important}
.bos-compact .bos-module-grid{grid-template-columns:1fr!important;gap:0!important}
.bos-compact .bos-module-card{min-height:0!important;border:0!important;border-bottom:1px solid #e8eeea!important;border-radius:0!important;box-shadow:none!important;background:white!important;display:grid!important;grid-template-columns:1fr 220px!important;padding:14px 0!important;gap:5px 16px!important;transform:none!important}
.bos-compact .bos-module-topline{grid-column:2;grid-row:1/3;justify-content:flex-end!important}.bos-compact .bos-module-dot{display:none}.bos-compact .bos-module-card>strong{grid-column:1;grid-row:1;font-size:13px!important;font-weight:600!important}.bos-compact .bos-module-card>small{grid-column:1;font-weight:400!important}.bos-compact .bos-module-topline em{font-size:10px!important;font-weight:600!important}
.bos-compact .bos-switch{border:0!important;border-bottom:1px solid #e8eeea!important;border-radius:0!important;box-shadow:none!important;background:white!important;min-height:58px!important;padding:10px 0!important}.bos-compact .bos-switch strong{font-weight:600!important}.bos-compact .bos-switch em{font-weight:400!important}
.bos-compact .bos-field select,.bos-compact .bos-field input{border-radius:7px!important;font-weight:500!important}.bos-compact summary{cursor:pointer;font-size:14px;font-weight:600;color:#235743}.bos-compact .bos-grid{margin-top:18px}.bos-compact .bos-note{font-weight:400!important;border-radius:6px!important}
@media(max-width:600px){.bos-compact .bos-module-card{grid-template-columns:1fr!important}.bos-compact .bos-module-topline{grid-column:1;grid-row:3;justify-content:flex-start!important}}
`;
